import React, { useState, useMemo } from "react";
import { StudyMaterial, StudyMaterialFilter, StudyMaterialFile } from "../../../../types/dataModel";
import { formatVideoDuration, getFormattedDateString } from "../../../../common/Utils";
import CommonDropdown from "../../../../core/common/commonDropDown";
import DeleteModalV2 from "../../../../core/modals/deleteModalV2";
import VideoPlaybackModal from "./videoPlaybackModal";
import YoutubeStudyMaterialCard, { isYouTubeLink } from "./youtubeStudyMaterialCard";
import RumbleStudyMaterialCard, { isRumbleLink } from "./rumbleStudyMaterialCard";

interface MaterialGroup {
  key: string;
  title: string;
  materials: StudyMaterial[];
  isSyllabusGroup: boolean;
}

const isSyllabusLevelMaterial = (material: StudyMaterial) =>
  material.syllabusId != null && material.unitId == null && material.sectionId == null;

const sortByDisplayOrder = (items: StudyMaterial[]) =>
  [...items].sort((a, b) => (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity));

const matchesMaterialFilter = (material: StudyMaterial, filter: StudyMaterialFilter) => {
  if (filter.contentType && filter.contentType !== 'all' && material.materialType !== filter.contentType) {
    return false;
  }
  if (filter.visibility && filter.visibility !== 'all') {
    if (filter.visibility === 'public' && !material.public) return false;
    if (filter.visibility === 'private' && material.public) return false;
  }
  if (filter.searchQuery) {
    const query = filter.searchQuery.toLowerCase();
    const titleMatch = (material.title ?? '').toLowerCase().includes(query);
    const contentMatch = material.content?.toLowerCase().includes(query) || false;
    if (!titleMatch && !contentMatch) return false;
  }
  return true;
};

const getMaterialGroupKey = (material: StudyMaterial): { key: string; title: string } => {
  if (material.sectionId != null) {
    return {
      key: `section-${material.sectionId}`,
      title: `Section: ${material.sectionName || `Section ${material.sectionId}`}`,
    };
  }
  if (material.unitId != null) {
    return {
      key: `unit-${material.unitId}`,
      title: `Unit: ${material.unitName || `Unit ${material.unitId}`}`,
    };
  }
  return { key: 'other', title: 'Other' };
};

interface Props {
  materials: StudyMaterial[];
  viewContext?: 'SYLLABUS' | 'UNIT' | 'SECTION';
  canReorder?: boolean;
  isReordering?: boolean;
  onReorder?: (reorderedMaterials: StudyMaterial[]) => void;
  onEdit: (material: StudyMaterial) => void;
  onDelete: (material: StudyMaterial) => void;
  filter: StudyMaterialFilter;
  onFilterChange: (filter: StudyMaterialFilter) => void;
}

const StudyMaterialList = ({
  materials,
  viewContext,
  canReorder = false,
  isReordering = false,
  onReorder,
  onEdit,
  onDelete,
  filter,
  onFilterChange,
}: Props) => {
  const [dragSourceIndex, setDragSourceIndex] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{
    show: boolean;
    material: StudyMaterial | null;
  }>({
    show: false,
    material: null
  });
  
  const [videoPlayback, setVideoPlayback] = useState<{
    show: boolean;
    videoFile: StudyMaterialFile | null;
    materialTitle: string;
  }>({
    show: false,
    videoFile: null,
    materialTitle: ''
  });



  const filteredMaterials = useMemo(() => {
    return sortByDisplayOrder(materials.filter((material) => matchesMaterialFilter(material, filter)));
  }, [materials, filter]);

  const draggableMaterials = useMemo(() => {
    const sorted = sortByDisplayOrder(materials);
    if (viewContext === 'SYLLABUS') {
      return sorted.filter(isSyllabusLevelMaterial);
    }
    if (viewContext === 'UNIT' || viewContext === 'SECTION') {
      return sorted;
    }
    return [];
  }, [materials, viewContext]);

  const filteredDraggableMaterials = useMemo(() => {
    return draggableMaterials.filter((material) => matchesMaterialFilter(material, filter));
  }, [draggableMaterials, filter]);

  const materialGroups = useMemo((): MaterialGroup[] | null => {
    if (viewContext !== 'SYLLABUS') return null;

    const groups: MaterialGroup[] = [];
    const groupMap = new Map<string, MaterialGroup>();

    const syllabusMaterials = sortByDisplayOrder(filteredMaterials.filter(isSyllabusLevelMaterial));
    if (syllabusMaterials.length > 0) {
      groups.push({
        key: 'syllabus',
        title: 'Syllabus' + (syllabusMaterials[0]?.syllabusTitle ? `: ${syllabusMaterials[0]?.syllabusTitle}` : ''),
        materials: syllabusMaterials,
        isSyllabusGroup: true,
      });
    }

    filteredMaterials
      .filter((material) => !isSyllabusLevelMaterial(material))
      .forEach((material) => {
        const { key, title } = getMaterialGroupKey(material);
        if (!groupMap.has(key)) {
          const group: MaterialGroup = { key, title, materials: [], isSyllabusGroup: false };
          groupMap.set(key, group);
          groups.push(group);
        }
        groupMap.get(key)!.materials.push(material);
      });

    return groups;
  }, [filteredMaterials, viewContext]);


  const getMaterialIcon = (contentType: string) => {
    switch (contentType) {
      case 'NOTE':
        return 'ti ti-file-text';
      case 'FILE':
        return 'ti ti-file';
      case 'LINK':
        return 'ti ti-link';
      case 'VIDEO':
        return 'ti ti-video';
      default:
        return 'ti ti-file';
    }
  };

  const getMaterialIconColor = (contentType: string) => {
    switch (contentType) {
      case 'NOTE':
        return 'text-primary';
      case 'FILE':
        return 'text-success';
      case 'LINK':
        return 'text-warning';
      case 'VIDEO':
        return 'text-danger';
      default:
        return 'text-secondary';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDelete = () => {
    if (showDeleteConfirm.material) {
      onDelete(showDeleteConfirm.material);
    }
    setShowDeleteConfirm({show: false, material: null});
  };

  const handleVideoPlay = (videoFile: StudyMaterialFile, materialTitle: string) => {
    setVideoPlayback({
      show: true,
      videoFile: videoFile,
      materialTitle: materialTitle
    });
  };

  const handleCloseVideo = () => {
    setVideoPlayback({
      show: false,
      videoFile: null,
      materialTitle: ''
    });
  };

  const handleFileDownload = (file: StudyMaterialFile) => {
    if (file.fileUrl) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = file.fileUrl;
      link.download = file.fileName || file.originalFileName || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDragStart = (startIndex: number) => {
    setDragSourceIndex(startIndex);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (dropIndex: number) => {
    if (dragSourceIndex === null || dragSourceIndex === dropIndex) {
      setDragSourceIndex(null);
      return;
    }

    const sourceMaterial = filteredDraggableMaterials[dragSourceIndex];
    const targetMaterial = filteredDraggableMaterials[dropIndex];
    if (!sourceMaterial || !targetMaterial) {
      setDragSourceIndex(null);
      return;
    }

    const fullList = [...draggableMaterials];
    const sourceFullIndex = fullList.findIndex((m) => m.id === sourceMaterial.id);
    const targetFullIndex = fullList.findIndex((m) => m.id === targetMaterial.id);
    if (sourceFullIndex === -1 || targetFullIndex === -1) {
      setDragSourceIndex(null);
      return;
    }

    const updated = [...fullList];
    const [moved] = updated.splice(sourceFullIndex, 1);
    updated.splice(targetFullIndex, 0, moved);

    onReorder?.(updated);
    setDragSourceIndex(null);
  };

  const renderFilters = () => (
    <div className="row g-3 mb-3 justify-content-center">
      <div className="col-md-12">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Search materials..."
          value={filter.searchQuery}
          onChange={(e) => onFilterChange({ ...filter, searchQuery: e.target.value })}
        />
      </div>
      <div className="col-md-12 d-flex align-items-center justify-content-center">
   
        <CommonDropdown
          options={[{label: 'All Types', value: 'all'}, {label: 'Notes', value: 'NOTE'}, {label: 'Files', value: 'FILE'}, {label: 'Links', value: 'LINK'}, {label: 'Videos', value: 'VIDEO'}]}
          defaultValue={filter.contentType}
          value={filter.contentType}
          onChange={(val) => onFilterChange({ ...filter, contentType: val })}
        />

        <CommonDropdown
          options={[{label: 'All Visibility', value: 'all'}, {label: 'Public', value: 'public'}, {label: 'Private', value: 'private'}]}
          defaultValue={filter.visibility}
          value={filter.visibility}
          onChange={(val) => onFilterChange({ ...filter, visibility: val })}
        />
    
      </div>

    </div>
  );

  const renderMaterialCard = (
    material: StudyMaterial,
    dragIndex: number,
    isDraggable: boolean
  ) => {
    const showDrag = canReorder && isDraggable && !isReordering && dragIndex >= 0;
    const title = material.title ?? '';
    const thumbnailUrl = material.materialType === 'VIDEO' && material.metadata?.thumbnail_url
      ? material.metadata.thumbnail_url 
      : null;

    return (
      <div
        key={material.id}
        className={`card mb-3${dragSourceIndex === dragIndex ? ' opacity-50' : ''}`}
        draggable={showDrag}
        onDragStart={() => handleDragStart(dragIndex)}
        onDragOver={handleDragOver}
        onDrop={() => handleDrop(dragIndex)}
      >
        <div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-start">
            <div className="d-flex align-items-center flex-grow-1">
              {showDrag && (
                <i
                  className="ti ti-grip-vertical text-muted me-2"
                  style={{ cursor: 'grab' }}
                  title="Drag to reorder"
                />
              )}
              <div className="d-flex align-items-center me-3 justify-content-center">
                  <span className="badge bg-light text-dark me-2 d-flex align-items-center gap-1 fs-8 flex-column">
                     <i className={`${getMaterialIcon(material.materialType)} ${getMaterialIconColor(material.materialType)} fs-12`} />
                     {material.materialType}
                  </span>
                  <h6 className="mb-1"> {title.length > 20 
                  ? `${title.substring(0, 25)}...` 
                  : title || 'Untitled'
                  }</h6>
              </div>
            </div>
          </div>
        
        {material.content && material.materialType === 'NOTE' && (
          <div className="mt-2">
            <p className="text-muted mb-0 fs-13">
              {material.content.length > 150 
                ? `${material.content.substring(0, 150)}...` 
                : material.content
              }
            </p>
          </div>
        )}
        
        {material.content && material.materialType === 'LINK' && (
          isYouTubeLink(material.content) ? (
            <YoutubeStudyMaterialCard content={material.content} title={title} />
          ) : isRumbleLink(material.content) ? (
            <RumbleStudyMaterialCard
              content={material.content}
              title={title}
              metadata={material.metadata}
            />
          ) : (
            <div className="mt-2">
              <a href={material.content} target="_blank" rel="noopener noreferrer" className="text-primary fs-13">
                <i className="ti ti-external-link me-1" />
                {material.content}
              </a>
            </div>
          )
        )}
        
        {material.files && material.files.length > 0 && (
          <div className="mt-2">
            <div className="d-flex flex-wrap gap-2">
              {material.files.map((file) => {
                const isVideoFile = material.materialType === 'VIDEO' || 
                  file.fileType?.startsWith('video/') || 
                  file.fileName?.match(/\.(mp4|avi|mov|mkv|webm|ogg)$/i);
                
                return (
                  <div 
                    key={file.id} 
                    className="d-flex flex-column align-items-start bg-light p-2 rounded cursor-pointer gap-2"
                    onClick={isVideoFile ? () => handleVideoPlay(file, title) : () => handleFileDownload(file)}
                    style={{ cursor: 'pointer' }}
                  >
                    {thumbnailUrl && (
                        <div className="flex-shrink-0 me-3">
                          <img
                            src={thumbnailUrl}
                            alt={`${title} thumbnail`}
                            className="rounded"
                            style={{ width: "128px", height: "72px", objectFit: "cover" }}
                            onError={(e) => {
                              // Hide image if it fails to load (e.g., due to auth issues)
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                    )}
                  
                    <div className="d-flex row justify-content-between align-items-center">
                     
                      <div className="fs-12 fw-medium text-primary"> <i className={`${isVideoFile ? 'ti ti-video' : 'ti ti-file'} me-2 ${isVideoFile ? 'text-danger' : 'text-success'}`} /> {file.fileName}</div>
                      { file.fileSize && <div className="fs-11 text-muted">{formatFileSize(file.fileSize)}</div>}
                      {isVideoFile ? (
                        <div className="fs-11 text-primary">Click to play</div>
                      ) : (
                        <div className="fs-11 text-primary">Click to download</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="flex-grow-1 mt-2 border-top pt-2">
          <div className="d-flex align-items-center text-muted fs-12">
            <div className="d-flex  flex-column justify-content-between w-100">
                <span>
                  <i className="ti ti-calendar me-1" />
                  {getFormattedDateString(material.createdOn)}
                </span>
                <div className="mt-1 d-flex align-items-center">
                  <span className={`badge ${material.public ? 'bg-success' : 'bg-secondary'} me-2`} style={{ width: 'fit-content' }}>
                  {material.public ? 'Public' : 'Private'}
                  </span>
                  {material?.metadata?.preview && <span className={`badge bg-danger me-2`} style={{ width: 'fit-content' }}>
                    Preview
                  </span>}
                  {material?.metadata?.duration && <span className={`text-muted fs-12 me-2`} style={{ width: 'fit-content' }}>
                    <i className="ti ti-clock me-1" />
                    {formatVideoDuration(material?.metadata?.duration)}
                  </span>}
                </div>
             </div>
             <div className="d-flex align-items-center">
              <button
                className="btn btn-sm btn-outline-primary me-2"
                onClick={() => onEdit(material)}
              >
                <i className="ti ti-edit" />
              </button>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => setShowDeleteConfirm({show: true, material: material})}
              >
                <i className="ti ti-trash" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  return (
    <div>
      {renderFilters()}
      
      {filteredMaterials.length === 0 ? (
        <div className="text-center py-5">
          <i className="ti ti-file-off fs-48 text-muted mb-3" />
          <h6 className="text-muted">No study materials found</h6>
          <p className="text-muted fs-12">Try adjusting your filters or add new materials</p>
        </div>
      ) : materialGroups ? (
        <div>
          {materialGroups.map((group, index) => (
            <div
              key={group.key}
              className={
                group.isSyllabusGroup
                  ? undefined
                  : 'rounded p-3 mb-3 bg-secondary bg-opacity-10 border border-secondary border-opacity-25'
              }
            >
              <h6
                className={`text-uppercase fs-12 mb-2 ${index === 0 ? 'mt-0' : 'mt-3'} ${
                  group.isSyllabusGroup ? 'text-muted' : 'text-secondary'
                }`}
              >
                {group.title}
              </h6>
              {group.materials.map((material) =>
                renderMaterialCard(
                  material,
                  group.isSyllabusGroup
                    ? filteredDraggableMaterials.findIndex((m) => m.id === material.id)
                    : -1,
                  group.isSyllabusGroup
                )
              )}
            </div>
          ))}
        </div>
      ) : (
        <div>
          {filteredMaterials.map((material) =>
            renderMaterialCard(
              material,
              filteredDraggableMaterials.findIndex((m) => m.id === material.id),
              true
            )
          )}
        </div>
      )}
      
      
       <DeleteModalV2
        show={showDeleteConfirm.show}
        heading="Delete Study Material"
        message="Are you sure you want to delete this study material? This action cannot be undone."
        onClose={() => setShowDeleteConfirm({show: false, material: null})}
        onDelete={() => handleDelete()}
      />
      
      <VideoPlaybackModal
        show={videoPlayback.show}
        onHide={handleCloseVideo}
        videoFile={videoPlayback.videoFile}
        materialTitle={videoPlayback.materialTitle}
      />
    </div>
  );
};

export default StudyMaterialList;
